/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-type is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

// NewUser page components
import FormField from "modules/inspections/components/FormField";
import { useEffect, useState } from "react";
import { Divider } from "@mui/material";
import SoftSelect from "components/SoftComponents/SoftSelect";
import SoftButton from "components/SoftComponents/SoftButton";

import { point, distance } from "@turf/turf";
import DeviceSelectionCard from "components/Cards/DeviceSelectionCard";

import { filterOptions, calcDistanceSuffix } from "utils/inspection";
import { actionItem, deviceDisplay } from "modules/inspections/utils/display";

function checkActionsValues(values, checkSingle = false, secondaryDeviceContent) {
    var labels = [
        "Removed",
        "Installation",
        "Unit Clean",
        "Battery Swap",
        "Hardware Changed",
        "Cosmetically Fixed",
        "Other Repair"
    ];

    var actions = [
        values['devicePrimary-actions-removed'],
        values['devicePrimary-actions-installed'],
        values['devicePrimary-actions-cleaned'],
        values['devicePrimary-actions-battery_swap'],
        values['devicePrimary-actions-hardware_changed'],
        values['devicePrimary-actions-cosmetically_fixed'],
        values['devicePrimary-actions-other'].length > 0
    ]

    if (checkSingle) {
        var is_true = actions.map((r, index) => { return { i: index, v: r } }).filter(r => r.v == true);
        if (is_true.length >= 1) {
            if (labels[is_true[0].i] == "Removed") {
                return (secondaryDeviceContent ? "Unit Replacement" : "Unit Decommission");
            }
            else if (is_true.length == 2) {
                return is_true.map(r => labels[r.i]).join(" + ");
            }
            else if (is_true.length == 1) {
                return labels[is_true[0].i];
            }

            return "Rectification & Completed Actions";
        }
    }
    else {
        return actions.filter(r => r == true).length > 0;
    }

    return false;
}

const checkWorks = (values, actionsOther, setFieldValue, inspection_type) => {
    const hasStandardAction = checkActionsValues(values);

    const removedField = values['devicePrimary-actions-removed'];
    const otherFieldHasText = values['devicePrimary-actions-other']?.trim().length > 0;
    const secondaryDeviceContent = values['deviceSecondary-uuid']?.trim().length > 0;

    const inspectionTitle = checkActionsValues(values, true, secondaryDeviceContent);
    setFieldValue(inspection_type.name, inspectionTitle ? inspectionTitle : "General Site Visit");

    if (actionsOther) {
        return otherFieldHasText && (!secondaryDeviceContent || (removedField && secondaryDeviceContent));
    }

    return hasStandardAction && (!secondaryDeviceContent || (removedField && secondaryDeviceContent));
}

function TechnicianActions({ formData, deviceLayout, userPermissions }) {
    const {
        values, touched, errors, formField, isSubmitting,
        functions: { setDeviceOptions, setFetchedUuids, setFieldValue, handleChange, handleBack, handleCancel },
        custom: { fetchedUuids, fetchDevices, activeStep, isLastStep, user, all_devices, inspection_record, deviceOptions }
    } = formData;
    const { devicePrimary, deviceSecondary, inspection_type } = formField;

    const [works, setWorks] = useState(false);
    const [showDevicePrimary, setShowDevicePrimary] = useState(false);
    const [currentLocation, setCurrentLocation] = useState(values.location || null);
    const [removedState, setRemovedState] = useState(values?.['devicePrimary-actions-removed'] || false);
    const [actionsOther, setActionsOther] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;

    useEffect(() => {
        setWorks(checkWorks(values, actionsOther, setFieldValue, inspection_type));
        if (!removedState) {
            handleClick(null);
        }
    }, [values, actionsOther]);

    useEffect(() => {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                setCurrentLocation([latitude, longitude]);
                setIsLoading(false);
            },
            (error) => {
                console.error("Error detecting location:", error);
                // Check if the error was specifically a timeout

                if (error.code === error.TIMEOUT) {
                    console.warn("Location request timed out. Setting to 0,0.");
                }
                setCurrentLocation([0, 0]);
                setIsLoading(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 15000, // 15 seconds in milliseconds
                maximumAge: 0   // Force fresh location
            }
        );
    }, []);

    useEffect(() => {
        if (!isLoading) {
            const origin = point(currentLocation);
            const options = {
                units: "metres"
            };

            function locationSort(a, b) {
                if (currentLocation[0] == 0 && currentLocation[1] == 0) {
                    return a.serial_number.localeCompare(b.serial_number, 'en', { numeric: true });
                }
                else {
                    const pointA = point([a.position[1], a.position[0]]);
                    const pointB = point([b.position[1], b.position[0]]);

                    const distanceA = distance(origin, pointA, options);
                    const distanceB = distance(origin, pointB, options);

                    return distanceA - distanceB;
                }
            }

            const device_options = all_devices.sort(locationSort).map((device) => {
                return {
                    metadata: device,
                    value: {
                        name: device.name,
                        serial_number: device.serial_number,
                        stop_id: device.stop.site_id,
                        uuid: device.uuid,
                        distance_away: (!currentLocation[0] && !currentLocation[1]) ? null : distance(origin, point([Number(device.position[1]), Number(device.position[0])]), options)
                    },
                    label: `${device.name} ${typeof device?.stop?.site_id != 'undefined' ? ('(' + device.stop.site_id + ')') : ""}`
                }
            });

            setDeviceOptions(device_options);
        }

    }, [isLoading]);

    const handleClick = (selected, e = false) => {
        // debugger;
        if (e) e.stopPropagation();
        setFieldValue(deviceSecondary.info.name, selected ? selected : "");
        setFieldValue(deviceSecondary.uuid.name, selected ? selected.value.uuid : "");
        setFieldValue(deviceSecondary.stop_id.name, selected ? selected.value.stop_id : "");
        setFieldValue(deviceSecondary.serial_no.name, selected ? selected.value.serial_number : "");

        setFieldValue(deviceSecondary.actions.installed.name, selected ? true : false);
        setFieldValue(deviceSecondary.actions.cleaned.name, selected ? true : false);

        setFetchedUuids(selected ? [...fetchedUuids, selected.value.uuid] : fetchedUuids);
    }

    const resetActions = (voided = false) => {
        var action_types = ['removed', 'installed', 'cleaned', 'battery_swap', 'hardware_changed', 'cosmetically_fixed', 'other'];
        if (voided) action_types = action_types.filter((row) => row != voided);

        for (var type of action_types) {
            setFieldValue(devicePrimary.actions[type].name, type == 'other' ? "" : false);
        }

        setFieldValue(inspection_type.name, "Assignment Update");
    }

    return (
        <>
            <SoftBox px={{ xxs: 1, sm: 3 }} pt={{ xxs: 2, sm: 3 }} pb={{ xxs: 0, xl: 3 }}>
                <SoftBox>
                    <SoftBox mb={2} display="flex" flexDirection="column" alignItems="center">
                        <SoftTypography variant="h4" fontWeight="bold">
                            Maintenance / Repairs
                        </SoftTypography>
                        <SoftTypography pt={1} variant="mobileBody1" textAlign="center">
                            Has there been any device works performed?
                        </SoftTypography>
                    </SoftBox>
                    <Divider />
                    <SoftBox mt={1}>
                        <SoftBox display="flex" flex={1} flexDirection='column'>
                            {deviceDisplay(values, isLastStep, 'devicePrimary', fetchDevices?.[values['devicePrimary-uuid']], showDevicePrimary, setShowDevicePrimary, showAdvancedDeviceLevel)}
                            <SoftBox mt={1} mb={2}>
                                <Divider />
                            </SoftBox>
                            <SoftBox display="flex" justifyContent="center" pb={2}>
                                <SoftTypography px={0.25} variant={deviceLayout == "mobile" ? "mobileBody1" : "subtitle2"} fontWeight={deviceLayout == "mobile" ? "bold" : "regular"} sx={{ textAlign: 'center' }}>
                                    Please select the rectifications you have made, or select 'Other' to add another. If none apply, skip this section.
                                </SoftTypography>
                            </SoftBox>
                            <Grid container spacing={1}>
                                {actionItem('assignment', 'removed', 'Removed/Replaced', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                {actionItem('assignment', 'cleaned', 'Cleaned', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                {actionItem('assignment', 'battery_swap', 'Battery Swap', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                {actionItem('assignment', 'hardware_changed', 'Hardware Changed', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                {actionItem('assignment', 'cosmetically_fixed', 'Cosmetically Fixed', values, devicePrimary, setFieldValue, setRemovedState, deviceLayout)}
                                <Grid item xxs={6}>
                                    <SoftBox>
                                        <SoftBox display="flex" justifyContent="center" py={3} px={1}
                                            onClick={(e) => {
                                                setActionsOther(!actionsOther);
                                            }}
                                            sx={({ palette: { gradients }, functions: { rgba, linearGradient } }) => ({
                                                borderRadius: '10px',
                                                cursor: 'pointer',
                                                background: linearGradient(rgba(gradients['light'].main, 0.6), actionsOther ? rgba(gradients['success'].main, 0.2) : rgba(gradients['light'].state, 0.6)),
                                                border: '1px solid #aaa'
                                            })}>
                                            <SoftBox display="flex" alignItems="center" sx={{ paddingTop: '2.5px' }}>
                                                <SoftTypography variant={deviceLayout == "mobile" ? "mobileBody1" : "body2"} fontWeight={'bold'}>Other</SoftTypography>
                                            </SoftBox>
                                        </SoftBox>
                                    </SoftBox>
                                </Grid>
                                {actionsOther && (
                                    <Grid item xxs={12}>
                                        <SoftBox>
                                            <FormField
                                                type={devicePrimary.actions.other.type}
                                                label={devicePrimary.actions.other.label}
                                                name={devicePrimary.actions.other.name}
                                                value={values['devicePrimary-actions-other']}
                                                placeholder={devicePrimary.actions.other.placeholder}
                                                onChange={handleChange}
                                            />
                                        </SoftBox>
                                    </Grid>
                                )}
                            </Grid>
                        </SoftBox>
                    </SoftBox>
                </SoftBox>
                {removedState && (
                    <SoftBox mt={4}>
                        <Grid container spacing={3}>
                            <Grid item xxs={12} lg={6}>
                                <SoftBox display="flex" flexDirection="column" alignItems={{ xxs: "center", lg: 'flex-start' }}>
                                    <SoftTypography variant="h4" fontWeight="bold" textAlign={{ xxs: "center", lg: "left" }}>
                                        Replacement Search
                                    </SoftTypography>
                                </SoftBox>
                                <SoftBox mb={1} display="flex" flexDirection="column" alignItems={{ xxs: "center", lg: 'flex-start' }}>
                                    <SoftTypography pt={1} variant="mobileBody1" textAlign={{ xxs: "center", lg: "left" }}>
                                        If there is a <b>replacement</b> device, type to search by:<br /><b>Device Name/Stop Name</b>, <b>Stop ID</b>, <b>Serial Number</b>, or <b>Site ID</b>
                                    </SoftTypography>
                                </SoftBox>
                                <SoftBox mb={2} sx={({ palette: { white }, borders: { borderRadius } }) => ({ maxWidth: '100%', borderRadius: borderRadius.md, backgroundColor: white.main })}>
                                    <SoftSelect
                                        name={deviceSecondary.info.name}
                                        value={deviceOptions.find(opt => opt.label === values['deviceSecondary-info']?.label) || ""}
                                        onChange={(selected) => handleClick(selected)}
                                        filterOption={filterOptions}
                                        options={deviceOptions}
                                        isClearable={true}
                                        isSearchable={true}
                                        placeholder="Search or select a device..."
                                        error={errors.deviceSecondary?.info && touched.deviceSecondary?.info}
                                    />
                                </SoftBox>
                            </Grid>
                            <Grid item xxs={12} lg={6}>
                                {typeof values['deviceSecondary-info'] != 'string' ? (
                                    <DeviceSelectionCard
                                        title={values['deviceSecondary-info']?.label}
                                        description={[`Stop ID: ${values['deviceSecondary-stop_id']}`, `Serial No.: ${values['deviceSecondary-serial_no']}`]}
                                        locations={{ user_loc: currentLocation, dest_loc: values['deviceSecondary-info']?.metadata.position }}
                                        action={{ func: isSubmitting, type: "submit", label: "select" }}
                                        value={calcDistanceSuffix(values['deviceSecondary-info']?.value.distance_away)}
                                    />
                                ) : (
                                    <DeviceSelectionCard />
                                )}
                            </Grid>
                        </Grid>
                    </SoftBox>
                )}
            </SoftBox>
            <SoftBox mt={3}>
                <Divider />
                <SoftBox sx={{ background: '#f9f9f9', borderRadius: '0 0 16px 16px' }} display='flex' justifyContent='space-between' p={1}>
                    <SoftButton variant="gradient" color={activeStep === 0 ? "error" : "light"} onClick={activeStep === 0 ? handleCancel : handleBack}>
                        {activeStep === 0 ? "cancel" : "back"}
                    </SoftButton>
                    {works ? (
                        <SoftButton
                            disabled={isSubmitting}
                            type="submit"
                            variant="gradient"
                            color="dark"
                        >
                            {isLastStep ? "submit" : "next"}
                        </SoftButton>
                    ) : (
                        <SoftButton
                            disabled={isSubmitting || (actionsOther && !values['devicePrimary-actions-other']?.trim().length)}
                            type="submit"
                            variant="gradient"
                            color="dark"
                            onClick={(e) => { resetActions(); }}
                        >
                            {isLastStep ? "submit" : "skip"}
                        </SoftButton>
                    )}
                </SoftBox>
            </SoftBox>
        </>
    );
}

// typechecking props for TechnicianActions
TechnicianActions.propTypes = {
    formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
    deviceLayout: PropTypes.string,
};

export default TechnicianActions;
